/**
 * INSTALLING THE APP ON A PHONE (desk, 2026-10-09: "make manifest so approver install the app in their mobile; use logo for app icon").
 *
 * The browser decides whether the site is installable from `public/manifest.webmanifest` (name, hospital-emblem icons, start page), `public/sw.js`
 * (a do-nothing worker that Android Chrome wants) and HTTPS. When it is, Chrome fires `beforeinstallprompt` ONCE, early, usually before any screen has
 * mounted - so it is caught here at start-up, kept, and handed to whichever screen wants to offer an "Install" button. Safari on iPhone never fires it:
 * there the only way is Share > Add to Home Screen, which the banner explains.
 */
import { useSyncExternalStore } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((listener) => listener());

/** Call once at start-up. Safe to call twice on the same window (the second call adds nothing new that matters). */
export function captureInstallPrompt(target: Window = window): void {
  target.addEventListener('beforeinstallprompt', (event) => {
    // Hold it: Chrome's own mini-bar is replaced by our banner, shown where an approver will see it.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    emit();
  });
  target.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    emit();
  });
}

/** True when the app is already running as an installed app (so no banner is wanted). */
export function isRunningInstalled(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

/** iPhone / iPad Safari: no install event exists, so the screen shows the manual steps instead of a button. */
export function isIosBrowser(userAgent: string = navigator.userAgent): boolean {
  return /iPad|iPhone|iPod/.test(userAgent);
}

export interface InstallPromptState {
  /** The browser has offered to install and the person has not yet answered. */
  canInstall: boolean;
  /** Opens the browser's install dialog. */
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

async function install(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  emit();
  await event.prompt();
  const { outcome } = await event.userChoice;
  if (outcome === 'accepted') installed = true;
  return outcome;
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function useInstallPrompt(): InstallPromptState {
  const canInstall = useSyncExternalStore(subscribe, () => deferred !== null && !installed);
  return { canInstall, install };
}

/** Registers the do-nothing worker, in the real build only (a dev server must not be intercepted). A failure is silent: the app works without it. */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const register = (): void => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
  };
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** Test seam: forgets what was captured. */
export function resetInstallPromptForTests(): void {
  deferred = null;
  installed = false;
  emit();
}
